import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  IconButton,
  Tooltip,
  Snackbar,
  Chip,
  Divider
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import { UseApi } from '../Hooks/UseApi';

/**
 * RequestDepositDialog
 * 
 * Opens a dialog for the artist to request a deposit for an appointment.
 * Can be used from the appointment detail dialog or the appointments list.
 * 
 * Props:
 *   open (bool) - Whether the dialog is open
 *   onClose (func) - Called when dialog closes
 *   appointment (object) - The appointment object (must have id, client_name, date, time, deposit_amount, deposit_paid, client_email)
 *   defaultDepositAmount (number|null) - Artist's default deposit amount from settings
 *   onDepositRequested (func) - Called after successful deposit request with updated appointment data
 */
const RequestDepositDialog = ({ 
  open, 
  onClose, 
  appointment, 
  defaultDepositAmount = null,
  onDepositRequested 
}) => {
  const api = UseApi();
  
  const [depositAmount, setDepositAmount] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [copied, setCopied] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Set initial deposit amount when dialog opens
  useEffect(() => {
    if (open && appointment) {
      // Priority: existing deposit amount on appointment > default > empty
      if (appointment.deposit_amount && parseFloat(appointment.deposit_amount) > 0) {
        setDepositAmount(String(appointment.deposit_amount));
      } else if (defaultDepositAmount && parseFloat(defaultDepositAmount) > 0) {
        setDepositAmount(String(defaultDepositAmount));
      } else {
        setDepositAmount('');
      }
      
      // Reset state
      setError(null);
      setCheckoutUrl(null);
      setCopied(false);
      setEmailSent(false);
      setSendEmail(true);
    }
  }, [open, appointment, defaultDepositAmount]);

  const handleRequestDeposit = async () => {
    if (!depositAmount || parseFloat(depositAmount) < 1) {
      setError('Please enter a deposit amount of at least $1.00');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.fetchWithAuth(
        `/appointments/${appointment.id}/deposit-checkout/`,
        {
          method: 'POST',
          body: JSON.stringify({
            appointment_id: appointment.id,
            deposit_amount: parseFloat(depositAmount),
            send_email: sendEmail,
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        setCheckoutUrl(data.checkout_url);
        setEmailSent(data.email_sent || false);
        
        // Notify parent component
        if (onDepositRequested) {
          onDepositRequested({
            ...appointment,
            deposit_amount: depositAmount,
          });
        }
      } else {
        setError(data.error || 'Failed to create deposit link. Please try again.');
      }
    } catch (err) {
      setError('An error occurred. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (checkoutUrl) {
      try {
        await navigator.clipboard.writeText(checkoutUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = checkoutUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      }
    }
  };

  const handleClose = () => {
    setCheckoutUrl(null);
    setError(null);
    setLoading(false);
    onClose();
  };

  if (!appointment) return null;

  const clientName = appointment.client_name || 
    `${appointment.first_name || ''} ${appointment.last_name || ''}`.trim() || 
    'Client';

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AttachMoneyIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              Request Deposit
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {/* Appointment info summary */}
          <Box sx={{ 
            bgcolor: 'grey.50', 
            borderRadius: 2, 
            p: 2, 
            mb: 3,
            border: '1px solid',
            borderColor: 'grey.200'
          }}>
            <Typography variant="body2" color="text.secondary">Appointment</Typography>
            <Typography variant="body1" fontWeight="bold">{clientName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {appointment.date} at {appointment.time}
            </Typography>
            {appointment.deposit_paid && (
              <Chip 
                icon={<CheckCircleIcon />} 
                label="Deposit Already Paid" 
                color="success" 
                size="small" 
                sx={{ mt: 1 }} 
              />
            )}
          </Box>

          {appointment.deposit_paid ? (
            <Alert severity="success">
              The deposit for this appointment has already been paid. No further action is needed.
            </Alert>
          ) : !checkoutUrl ? (
            // Step 1: Enter amount and options
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              <TextField
                label="Deposit Amount"
                type="number"
                value={depositAmount}
                onChange={(e) => setDepositAmount(e.target.value)}
                fullWidth
                required
                InputProps={{
                  startAdornment: <AttachMoneyIcon sx={{ color: 'text.secondary', mr: 0.5 }} />,
                  inputProps: { min: 1, step: 5 }
                }}
                sx={{ mb: 2 }}
                helperText={
                  defaultDepositAmount 
                    ? `Your default deposit amount: $${parseFloat(defaultDepositAmount).toFixed(2)}`
                    : 'Enter the deposit amount for this appointment'
                }
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">
                      Email payment link to client
                    </Typography>
                    {appointment.client_email && (
                      <Typography variant="caption" color="text.secondary">
                        Will be sent to: {appointment.client_email}
                      </Typography>
                    )}
                    {!appointment.client_email && (
                      <Typography variant="caption" color="warning.main">
                        No email on file — you'll need to share the link manually
                      </Typography>
                    )}
                  </Box>
                }
                sx={{ mb: 1 }}
              />

              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Payment is processed securely through Stripe. Funds go directly to your connected 
                Stripe account. The payment link expires in 24 hours.
              </Typography>
            </>
          ) : (
            // Step 2: Show the generated link
            <>
              <Alert severity="success" sx={{ mb: 2 }}>
                Deposit link created successfully!
                {emailSent && ' A payment email has been sent to the client.'}
              </Alert>

              {emailSent && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <EmailIcon color="success" fontSize="small" />
                  <Typography variant="body2" color="success.main">
                    Email sent to {appointment.client_email}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>
                <LinkIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                Share this link with your client:
              </Typography>

              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                bgcolor: 'grey.50',
                borderRadius: 2,
                p: 1.5,
                border: '1px solid',
                borderColor: 'grey.300'
              }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    flex: 1, 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: 'primary.main'
                  }}
                >
                  {checkoutUrl}
                </Typography>
                <Tooltip title={copied ? "Copied!" : "Copy link"}>
                  <IconButton 
                    onClick={handleCopyLink}
                    color={copied ? "success" : "primary"}
                    size="small"
                  >
                    {copied ? <CheckCircleIcon /> : <ContentCopyIcon />}
                  </IconButton>
                </Tooltip>
              </Box>

              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={handleCopyLink}
                fullWidth
                sx={{ mt: 2 }}
                color={copied ? "success" : "primary"}
              >
                {copied ? 'Link Copied!' : 'Copy Payment Link'}
              </Button>

              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                This link expires in 24 hours. The client will see a secure Stripe checkout 
                page for ${parseFloat(depositAmount).toFixed(2)}.
              </Typography>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          {!checkoutUrl && !appointment.deposit_paid ? (
            <>
              <Button onClick={handleClose} disabled={loading}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleRequestDeposit}
                disabled={loading || !depositAmount}
                startIcon={loading ? <CircularProgress size={16} /> : <AttachMoneyIcon />}
              >
                {loading ? 'Creating Link...' : 'Generate Payment Link'}
              </Button>
            </>
          ) : (
            <Button onClick={handleClose} variant="contained">
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Copy success snackbar */}
      <Snackbar
        open={copied}
        autoHideDuration={3000}
        onClose={() => setCopied(false)}
        message="Payment link copied to clipboard!"
      />
    </>
  );
};

export default RequestDepositDialog;